import React, { useState } from "react";
import { CardDeck, CardsBack, useCardTabs } from "@/components/nds/TabCards";
import { Eye, Search } from "lucide-react";
import { toast } from "sonner";
import { AUD_LABEL, docsApi, docsErr } from "@/lib/docsApi";
import { useAuth } from "@/lib/auth";
import { btnGold, Chip, Empty, ErrorBox, field, Loading, useLoad } from "@/components/nds/windows/finance/parts";
import { SignList } from "@/components/docs/SignList";
import { LibraryTab } from "@/components/docs/LibraryTab";
import { GridTab } from "@/components/docs/GridTab";
import { PartnerLibrary } from "@/components/docs/PartnerLibrary";

function SignedTab() {
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [state, load] = useLoad(() => docsApi.get("/signed", { q: term }), [term]);
  const open = (id) => docsApi.openPdf(`/requests/${id}/signed-pdf`).catch((e) => toast.error(docsErr(e)));
  return (
    <div className="space-y-4" data-testid="docs-signed">
      <form onSubmit={(e) => { e.preventDefault(); setTerm(q); }} className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by person or document" data-testid="docs-signed-search" className={`${field} flex-1`} />
        <button type="submit" data-testid="docs-signed-search-btn" className={`${btnGold} inline-flex items-center gap-1`}><Search className="h-3 w-3" /> Search</button>
      </form>
      {state.loading && !state.data ? <Loading testid="docs-signed-loading" /> : state.error ? <ErrorBox testid="docs-signed-error" error={state.error} onRetry={load} /> : !state.data.signed.length
        ? <Empty testid="docs-signed-empty">No signed copies yet.</Empty> : (
          <ul className="divide-y divide-white/5 border border-white/10 rounded-sm">
            {state.data.signed.map((x) => <li key={x.id} data-testid={`docs-signed-row-${x.id}`} className="px-4 py-2.5 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px]"><div className="text-nex-silver">{x.title} <span className="text-xs text-nex-muted">v{x.version}</span></div>
                <div className="text-xs text-nex-muted">{x.person_name} · signed {x.signed_at_local}</div></div>
              <Chip tone="blue">{AUD_LABEL[x.person_type]}</Chip>
              <button type="button" onClick={() => open(x.id)} data-testid={`docs-signed-view-${x.id}`} className={`${btnGold} inline-flex items-center gap-1`}><Eye className="h-3 w-3" /> View</button>
            </li>)}
          </ul>)}
    </div>
  );
}

const TABS = [["mine", "To sign"], ["plib", "Document library"], ["grid", "Who's missing what"], ["library", "Library"], ["signed", "Signed copies"]];

/** The one Documents card: my documents to sign, who's missing what, the library, and signed copies. */
export default function DocsCenterContent() {
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin" || (user?.staff_permissions || []).includes("pmc");
  const isPartner = user?.role === "partner_user";
  const show = ([k]) => (k === "plib" ? isPartner : canEdit || k === "mine");
  const [mine, setMine] = useState(null);
  const ct = useCardTabs(canEdit ? "grid" : "mine");
  const tab = ct.tab;
  const setTab = ct.open;
  const DESC = { mine: "Documents waiting for your signature", grid: "Which residents are missing which documents", library: "Every template and form in the library", signed: "Signed copies you can view and download", plib: "What you signed, and your residents' documents" };
  const items = TABS.filter(show).map(([k, v]) => ({ id: k, label: v, desc: DESC[k], ...(k === "mine" && mine != null ? { stat: mine, note: "to sign", alert: mine > 0 } : {}) }));
  return (
    <div ref={ct.ref} className="p-5 space-y-5" data-testid="docs-center">
      {ct.deck && <CardDeck testid="docs-cards" items={items} onOpen={setTab} />}
      {ct.compact && !ct.deck && <CardsBack testid="docs-cards-back" className="" label={TABS.find(([k]) => k === tab)[1]} onClick={ct.close} />}
      {!ct.compact && <div className="flex flex-wrap gap-2" role="tablist">
        {TABS.filter(show).map(([k, v]) => <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`docs-tab-${k}`}
          className={`px-3 py-1.5 rounded-sm border font-mono text-[10px] tracking-[0.18em] uppercase ${tab === k ? "border-nex-gold text-nex-gold bg-nex-gold/10" : "border-white/10 text-nex-muted hover:text-nex-silver"}`}>
          {v}{k === "mine" && mine ? ` (${mine})` : ""}</button>)}
      </div>}
      <div className={tab === "mine" && !ct.deck ? "" : "hidden"}><SignList api={docsApi} base="/me" prefix="docs-mine" onCount={setMine} /></div>
      {!ct.deck && tab === "grid" && <GridTab />}
      {!ct.deck && tab === "library" && <LibraryTab canEdit={canEdit} />}
      {!ct.deck && tab === "signed" && <SignedTab />}
      {!ct.deck && tab === "plib" && <PartnerLibrary />}
    </div>
  );
}
